import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {jsonInit} from './client';
import {useAuth} from '../hooks/useAuth';
import {Post, PostInput, TodoSuggestions, TodosPage} from '../types';

// The To do tab. A to-do is a post (kind "reminder"), created and edited through /api/posts, but
// cached apart from the Journal's feed so it never slides into it. Query keys:
//   ['todos']                 open and recently done
//   ['todos', 'suggestions']  problems to check, jobs from other years

export function useTodos() {
    const {call} = useAuth();
    return useQuery({queryKey: ['todos'], queryFn: () => call<TodosPage>('/api/todos'), staleTime: 30 * 1000});
}

export function useTodoSuggestions() {
    const {call} = useAuth();
    return useQuery({queryKey: ['todos', 'suggestions'], queryFn: () => call<TodoSuggestions>('/api/todos/suggestions'), staleTime: 5 * 60 * 1000});
}

export type TodoInput = Omit<PostInput, 'kind'>;

export function useTodoMutations() {
    const {call} = useAuth();
    const queryClient = useQueryClient();
    const refresh = () => queryClient.invalidateQueries({queryKey: ['todos']});

    const create = useMutation({
        mutationFn: (input: TodoInput) => call<Post>('/api/posts', jsonInit('POST', {...input, kind: 'reminder'})),
        onSuccess: refresh,
        meta: {silent: true}
    });
    const patch = useMutation({
        mutationFn: ({id, ...input}: Partial<TodoInput> & {id: string}) => call<Post>(`/api/posts/${id}`, jsonInit('PATCH', {...input, kind: 'reminder'})),
        onSuccess: post => {
            queryClient.setQueryData(['posts', post.id], post);
            return refresh();
        },
        meta: {silent: true}
    });
    const done = useMutation({
        mutationFn: (id: string) => call<Post>(`/api/todos/${id}/done`, jsonInit('POST')),
        // Optimistic: the row slides out at once.
        onMutate: async id => {
            await queryClient.cancelQueries({queryKey: ['todos'], exact: true});
            queryClient.setQueryData<TodosPage>(['todos'], data => (data ? {...data, open: data.open.filter(t => t.id !== id)} : data));
        },
        onSettled: refresh
    });
    const reopen = useMutation({
        mutationFn: (id: string) => call<Post>(`/api/todos/${id}/reopen`, jsonInit('POST')),
        onSettled: refresh
    });
    const snooze = useMutation({
        mutationFn: ({id, dueLocal}: {id: string; dueLocal: string | null}) => call<Post>(`/api/todos/${id}/snooze`, jsonInit('POST', {dueLocal})),
        onSettled: refresh
    });
    const dismiss = useMutation({
        mutationFn: ({key, season}: {key: string; season: string}) => call<void>('/api/todos/dismiss', jsonInit('POST', {key, season})),
        onMutate: async ({key}) => {
            await queryClient.cancelQueries({queryKey: ['todos', 'suggestions']});
            queryClient.setQueryData<TodoSuggestions>(['todos', 'suggestions'], data =>
                data ? {...data, history: data.history.filter(h => h.key !== key), problems: data.problems.filter(p => `problem:${p.id}` !== key)} : data
            );
        },
        onSettled: () => queryClient.invalidateQueries({queryKey: ['todos', 'suggestions']})
    });

    return {create, patch, done, reopen, snooze, dismiss};
}
